import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getEmployeeForUser } from "@/lib/orgAccess";
import type { EmployeeMapsLocationState } from "@/types/employeeMapsNavigation";

type Props = { children: ReactNode };

const allowCache = new Map<string, boolean>();

function gateCacheKey(
  userId: string,
  employeeId: string,
  scopedOrganizationId: string | undefined,
): string {
  return `${userId}:${employeeId}:${scopedOrganizationId ?? ""}`;
}

/**
 * Ensures `/employee/:employeeId/*` routes only load when the signed-in user may view that roster row.
 */
export function EmployeeSessionGate({ children }: Props) {
  const { user, loading, isInitialized } = useAuth();
  const { employeeId } = useParams<{ employeeId: string }>();
  const location = useLocation();
  const scopedOrganizationId =
    (location.state as EmployeeMapsLocationState | null)?.scopedOrganizationId ?? undefined;
  const cacheKey =
    user?.id && employeeId ? gateCacheKey(user.id, employeeId, scopedOrganizationId) : null;
  const cachedAllowed = cacheKey ? allowCache.get(cacheKey) : undefined;

  const [allowed, setAllowed] = useState<boolean | null>(
    cachedAllowed !== undefined ? cachedAllowed : null,
  );

  useEffect(() => {
    if (!isInitialized || loading) return;
    if (!user || !employeeId) {
      setAllowed(false);
      return;
    }
    const key = gateCacheKey(user.id, employeeId, scopedOrganizationId);
    const hit = allowCache.get(key);
    if (hit !== undefined) {
      setAllowed(hit);
      return;
    }
    let cancelled = false;
    getEmployeeForUser(user, employeeId, { scopedOrganizationId })
      .then((row) => {
        const ok = Boolean(row);
        allowCache.set(key, ok);
        if (!cancelled) setAllowed(ok);
      })
      .catch(() => {
        allowCache.set(key, false);
        if (!cancelled) setAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, employeeId, loading, isInitialized, scopedOrganizationId]);

  if (!isInitialized || loading || allowed === null) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-[var(--color-text-muted)]">
        Loading…
      </div>
    );
  }

  if (!user || !allowed) {
    return <Navigate to="/org" replace />;
  }

  return <>{children}</>;
}
